import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Stack,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";

/** --- Netflix-like palette tokens (local to component) --- */
const NF = {
  bg: "#141414",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.78)",
  border: "rgba(255,255,255,0.14)",
  borderStrong: "rgba(255,255,255,0.22)",
  red: "#E50914",
  redHover: "#b20710",
  amber: "#ffa000",
  amberHover: "#cc8000",
  glow:
    "radial-gradient(900px 420px at 10% -10%, rgba(229,9,20,0.10), transparent 55%), radial-gradient(600px 300px at 110% 0%, rgba(255,255,255,0.05), transparent 60%)",
};

const DangerZone: React.FC<{
  onDelete: () => void;
  onDeactivate: () => void;
}> = ({ onDelete, onDeactivate }) => {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const handleAccordionChange = (_: React.SyntheticEvent, isExpanded: boolean) =>
    setExpanded(isExpanded);

  return (
    <Stack spacing={2} sx={{ fontFamily: "'Lexend', sans-serif", color: NF.text }}>
      <Accordion
        expanded={expanded}
        onChange={handleAccordionChange}
        sx={{
          boxShadow: "0 24px 60px rgba(0,0,0,.55)",
          borderRadius: 3,
          overflow: "hidden",
          background: `${NF.glow}, ${NF.bg}`,
          border: `1px solid ${NF.border}`,
          "&:before": { display: "none" },
          ".MuiAccordionSummary-root": {
            px: { xs: 2, sm: 3 },
            py: { xs: 1.25, sm: 1.5 },
          },
          ".MuiAccordionDetails-root": {
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2.5 },
            background: "linear-gradient(180deg, transparent, rgba(255,255,255,0.03))",
            borderTop: `1px solid ${NF.border}`,
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore htmlColor={NF.muted} />}
          sx={{
            "& .MuiAccordionSummary-content": { my: { xs: 0.25, sm: 0.5 } },
            "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
              transform: "rotate(180deg)",
            },
            transition: "background .2s ease",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.03)" },
          }}
        >
          <Stack spacing={0.5}>
            <Typography
              sx={{
                color: NF.red,
                fontWeight: 900,
                letterSpacing: ".2px",
                textShadow: "0 1px 0 rgba(0,0,0,.35)",
                fontSize: { xs: "1.05rem", sm: "1.15rem" },
              }}
            >
              Danger Zone
            </Typography>
            <Typography sx={{ color: NF.muted, fontSize: { xs: ".9rem", sm: ".95rem" } }}>
              Irreversible actions that will affect your account.
            </Typography>
          </Stack>
        </AccordionSummary>

        <AccordionDetails>
          <Stack
            direction={isSmDown ? "column" : "row"}
            spacing={isSmDown ? 1 : 1.25}
            alignItems={isSmDown ? "stretch" : "center"}
          >
            <Button
              variant="outlined"
              onClick={() => setDeactivateOpen(true)}
              fullWidth={isSmDown}
              sx={{
                textTransform: "none",
                fontWeight: 800,
                letterSpacing: ".2px",
                color: NF.amber,
                borderColor: NF.borderStrong,
                borderRadius: 2,
                px: { xs: 2, sm: 2.5 },
                py: { xs: 1, sm: 1.1 },
                minHeight: { xs: 40, sm: 44 },
                backgroundColor: "rgba(255,255,255,0.04)",
                "&:hover": {
                  borderColor: NF.amber,
                  backgroundColor: "rgba(255,255,255,0.08)",
                },
              }}
            >
              Deactivate Account
            </Button>

            <Button
              variant="contained"
              onClick={() => setDeleteOpen(true)}
              fullWidth={isSmDown}
              sx={{
                textTransform: "none",
                fontWeight: 900,
                letterSpacing: ".2px",
                color: NF.text,
                background: NF.red,
                borderRadius: 2,
                px: { xs: 2, sm: 2.5 },
                py: { xs: 1, sm: 1.1 },
                minHeight: { xs: 40, sm: 44 },
                boxShadow: "0 10px 28px rgba(229,9,20,.35)",
                "&:hover": { background: NF.redHover },
              }}
            >
              Delete Account
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Deactivate Dialog */}
      <Dialog
        open={deactivateOpen}
        onClose={() => setDeactivateOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            color: NF.text,
            background: `${NF.glow}, ${NF.bg}`,
            border: `1px solid ${NF.border}`,
            boxShadow: "0 24px 60px rgba(0,0,0,.55)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Deactivate Account?</DialogTitle>
        <DialogContent sx={{ color: NF.muted }}>
          You can reactivate later by logging in.
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 2,
            gap: 1,
            flexWrap: "wrap",
            background: "linear-gradient(180deg, transparent, rgba(255,255,255,0.03))",
          }}
        >
          <Button
            onClick={() => setDeactivateOpen(false)}
            sx={{
              textTransform: "none",
              color: NF.muted,
              borderRadius: 2,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              onDeactivate();
              setDeactivateOpen(false);
              enqueueSnackbar("Account deactivated successfully.", { variant: "success" });
            }}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 800,
              color: NF.amber,
              borderColor: NF.borderStrong,
              borderRadius: 2,
              px: 2,
              "&:hover": { borderColor: NF.amber, backgroundColor: "rgba(255,255,255,0.06)" },
            }}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            color: NF.text,
            background: `${NF.glow}, ${NF.bg}`,
            border: `1px solid ${NF.border}`,
            boxShadow: "0 24px 60px rgba(0,0,0,.55)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: NF.red }}>Delete Account?</DialogTitle>
        <DialogContent sx={{ color: NF.muted }}>
          This action is irreversible. All your data will be permanently removed.
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 2,
            gap: 1,
            flexWrap: "wrap",
            background: "linear-gradient(180deg, transparent, rgba(255,255,255,0.03))",
          }}
        >
          <Button
            onClick={() => setDeleteOpen(false)}
            sx={{
              textTransform: "none",
              color: NF.muted,
              borderRadius: 2,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              onDelete();
              setDeleteOpen(false);
              enqueueSnackbar("Account deleted successfully.", { variant: "success" });
            }}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 900,
              color: NF.text,
              background: NF.red,
              borderRadius: 2,
              px: 2,
              boxShadow: "0 10px 28px rgba(229,9,20,.35)",
              "&:hover": { background: NF.redHover },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default DangerZone;
